/**
 * Staff-side test viewer for the video-record scaffold.
 *  - "Create record link" makes a fresh token and shows the shareable phone URL.
 *  - Lists every uploaded temp video (newest first) in equal 16:9 tiles, plays
 *    them inline, and deletes one from storage via the floating button.
 *
 * Throwaway scaffold for a future module; API in nova-api testVideo.controller.js.
 */
import { useEffect, useState } from "react";
import authHeader from "services/auth-header";
import API from "services/axios";

const makeToken = () => {
  // 16 hex chars; crypto where available, Math.random fallback.
  try {
    const a = new Uint8Array(8);
    crypto.getRandomValues(a);
    return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return Math.random().toString(16).slice(2, 10) + Date.now().toString(16);
  }
};

const linkBase = () =>
  (process.env.REACT_APP_URI || window.location.origin).replace(/\/$/, "");

const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};

export default function TestVideos() {
  const [token, setToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null); // key in flight
  const [error, setError] = useState(null);

  const recordUrl = token ? `${linkBase()}/test/record/${token}` : "";

  const loadVideos = async () => {
    try {
      setLoading(true);
      setError(null);
      // The list is admin-only on the API, and the axios instance does not
      // attach the token by itself. Without this header the call is a 403,
      // and the response interceptor treats 403 as "logged out" -- so
      // opening this page silently signed the user out.
      const { data } = await API.get("/test-videos", { headers: authHeader() });
      setVideos(data?.videos || []);
    } catch (err) {
      setVideos([]);
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVideos();
  }, []);

  const createLink = () => {
    setToken(makeToken());
    setCopied(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(recordUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be blocked; user can select manually */
    }
  };

  // Deletes from storage, not just the list. A native confirm is enough on a
  // test page; the object is gone for good once it answers.
  const remove = async (v) => {
    if (!v?.key) return;
    if (!window.confirm("Delete this video from storage? This cannot be undone.")) return;
    try {
      setDeleting(v.key);
      setError(null);
      const { data } = await API.delete("/test-video", {
        headers: authHeader(),
        data: { key: v.key },
      });
      if (!data?.success) throw new Error(data?.message || "Delete failed");
      setVideos((list) => list.filter((x) => x.key !== v.key));
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="px-3 py-3" style={{ marginLeft: 260, minHeight: "100vh" }}>
      <h3 className="mb-3">Video Record — Test</h3>

      {/* Create + share a record link */}
      <div className="card border mb-4">
        <div className="card-body">
          <div className="d-flex align-items-center gap-2 mb-2">
            <button className="btn btn-primary" onClick={createLink}>
              Create Record Link
            </button>
            <span className="text-muted small">
              Send this link to a phone to record &amp; upload a video.
            </span>
          </div>

          {token && (
            <div className="d-flex align-items-center gap-2 mt-2">
              <input
                className="form-control"
                readOnly
                value={recordUrl}
                onFocus={(e) => e.target.select()}
              />
              <button className="btn btn-outline-secondary" onClick={copyLink}>
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Uploaded videos. Refresh sits by the heading, where it is seen. */}
      <div className="d-flex align-items-center gap-2 mb-2">
        <h5 className="mb-0">
          Uploaded Videos {videos.length ? `(${videos.length})` : ""}
        </h5>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={loadVideos}
          disabled={loading}
          title="Reload the list from storage"
        >
          <i className={`mdi mdi-refresh me-1 ${loading ? "mdi-spin" : ""}`} aria-hidden="true" />
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error && <div className="alert alert-danger py-2 px-3">{error}</div>}

      {loading && videos.length === 0 ? (
        <div className="text-muted">Loading…</div>
      ) : videos.length === 0 ? (
        <div className="text-muted">No videos uploaded yet.</div>
      ) : (
        <div className="row">
          {videos.map((v) => (
            <div className="col-md-4 mb-3" key={v.key || v.url}>
              {/* Every tile is the same 16:9 box whatever the clip's shape --
                  a portrait phone recording used to stretch its column to
                  the floor while the landscape ones stayed short. The clip
                  is letterboxed inside, never cropped. */}
              <div
                className="ratio ratio-16x9 position-relative bg-black"
                style={{ borderRadius: 12, overflow: "hidden" }}
              >
                <video
                  src={v.url}
                  controls
                  playsInline
                  preload="metadata"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
                <button
                  type="button"
                  className="btn btn-danger btn-sm rounded-circle position-absolute d-flex align-items-center justify-content-center"
                  // The ratio helper stretches every child to fill the box;
                  // pin this one to the corner and give it a fixed size.
                  style={{ top: 8, right: 8, left: "auto", bottom: "auto", width: 34, height: 34, zIndex: 2, opacity: 0.92 }}
                  onClick={() => remove(v)}
                  disabled={deleting === v.key}
                  title="Delete this video from storage"
                  aria-label="Delete video"
                >
                  <i className={`mdi ${deleting === v.key ? "mdi-loading mdi-spin" : "mdi-delete"}`} aria-hidden="true" />
                </button>
              </div>
              <div className="d-flex justify-content-between text-muted mt-1" style={{ fontSize: 12 }}>
                <span className="text-truncate" title={v.token || ""}>{v.token || "—"}</span>
                <span className="text-nowrap ms-2">{when(v.uploadedAt)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
